/* eslint-disable react/display-name */
import React, { useMemo, useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { restaurants, deleteRestaurant } from '../apollo'
import {
  Box, Container, Typography, Paper, InputBase,
  Button, CircularProgress, Chip, Snackbar, IconButton
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import StorefrontIcon from '@mui/icons-material/Storefront'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PersonIcon from '@mui/icons-material/Person'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import RefreshIcon from '@mui/icons-material/Refresh'
import StarIcon from '@mui/icons-material/Star'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const GET_RESTAURANTS = gql`${restaurants}`
const DELETE_RESTAURANT = gql`${deleteRestaurant}`
const GET_RESTAURANTS_BY_OWNER = gql`
  query RestaurantByOwner($id: String) {
    restaurantByOwner(id: $id) {
      restaurants {
        _id
        name
        image
        logo
        orderPrefix
        slug
        address
        deliveryTime
        minimumOrder
        isActive
        commissionRate
        tax
        shopType
        reviewCount
        owner { _id email }
      }
    }
  }
`

const RestaurantList = props => {
  const { t } = props

  // vendorFilterId is only valid when navigating directly from Merchants page.
  // The Vendors.jsx sets it immediately before pushing to this route, so it will
  // be present on the initial render. Clear it after reading so that subsequent
  // visits (e.g. clicking "Restaurants" in the sidebar) show all restaurants.
  const { vendorId, vendorName } = useMemo(() => {
    const id = localStorage.getItem('vendorFilterId') || null
    const name = localStorage.getItem('vendorFilterName') || ''
    localStorage.removeItem('vendorFilterId')
    localStorage.removeItem('vendorFilterName')
    return { vendorId: id, vendorName: name }
  }, [props.location?.key])

  const [searchQuery, setSearchQuery] = useState('')
  const [snackError, setSnackError] = useState(null)

  // Fetch all restaurants (used when no vendor filter)
  const { data, loading, refetch } = useQuery(GET_RESTAURANTS, {
    fetchPolicy: 'network-only',
    skip: !!vendorId
  })

  // Fetch only this vendor's restaurants when vendorId is set
  const { data: ownerData, loading: ownerLoading, refetch: refetchOwner } = useQuery(GET_RESTAURANTS_BY_OWNER, {
    variables: { id: vendorId },
    fetchPolicy: 'network-only',
    skip: !vendorId
  })

  const [mutate, { loading: mutateLoading }] = useMutation(DELETE_RESTAURANT, {
    refetchQueries: vendorId
      ? [{ query: GET_RESTAURANTS_BY_OWNER, variables: { id: vendorId } }]
      : [{ query: GET_RESTAURANTS }],
    onError: err => setSnackError(err.graphQLErrors?.[0]?.message || 'Something went wrong')
  })

  const allRestaurants = vendorId
    ? (ownerData?.restaurantByOwner?.restaurants ?? [])
    : (data?.restaurants ?? [])

  const isLoading = vendorId ? ownerLoading : loading
  const doRefetch = vendorId ? refetchOwner : refetch

  const activeCount = allRestaurants.filter(r => r.isActive).length
  const inactiveCount = allRestaurants.length - activeCount

  const filtered = useMemo(() => {
    if (searchQuery.length < 2) return allRestaurants
    const q = searchQuery.toLowerCase()
    return allRestaurants.filter(r =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.address || '').toLowerCase().includes(q) ||
      (r.owner?.email || '').toLowerCase().includes(q) ||
      (r.orderPrefix || '').toLowerCase().includes(q)
    )
  }, [allRestaurants, searchQuery])

  return (
    <>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 4 }}>
        {/* Back to Merchants — only shown when filtered by vendor */}
        {vendorId && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => props.history.push('/vendors')}
            size="small"
            sx={{ mb: 2, color: '#64748b', fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', px: 1.5, py: 0.6, '&:hover': { bgcolor: '#f1f5f9' } }}
          >
            Back to Merchants{vendorName ? ` · ${vendorName}` : ''}
          </Button>
        )}

        {/* Header row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <StorefrontIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#0f172a', lineHeight: 1.2 }}>Restaurants</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                {vendorId && vendorName ? `Showing restaurants for ${vendorName}` : 'Manage all restaurants on the platform'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {/* Stat chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', px: 2, py: 0.8 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1d4ed8' }}>{isLoading ? '…' : allRestaurants.length}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600 }}>Total</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', px: 2, py: 0.8 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#16a34a' }}>{isLoading ? '…' : activeCount}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>Active</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', px: 2, py: 0.8 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#d97706' }}>{isLoading ? '…' : inactiveCount}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>Inactive</Typography>
            </Box>
            <IconButton onClick={() => doRefetch()} size="small" sx={{ ml: 0.5, bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', '&:hover': { bgcolor: '#e2e8f0' } }}>
              <RefreshIcon sx={{ fontSize: 18, color: '#64748b' }} />
            </IconButton>
          </Box>
        </Box>

        {/* Search bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', px: 2, py: 1, mb: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
          <InputBase
            placeholder="Search by name, address, vendor email or prefix..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ fontSize: '0.875rem', flex: 1, color: '#475569' }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <StorefrontIcon sx={{ fontSize: 52, color: '#e2e8f0', mb: 1 }} />
            <Typography sx={{ color: '#94a3b8' }}>No restaurants found</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 2.5 }}>
            {filtered.map(r => (
              <Paper
                key={r._id}
                elevation={0}
                onClick={() => {
                  localStorage.setItem('restaurantId', r._id)
                  localStorage.setItem('restaurantImage', r.image)
                  localStorage.setItem('restaurantName', r.name)
                  props.history.push('/admin/dashboard/' + r.slug)
                }}
                sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.18s', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', '&:hover': { boxShadow: '0 8px 28px rgba(0,0,0,0.11)', transform: 'translateY(-2px)', borderColor: '#cbd5e1' } }}
              >
                {/* Image area */}
                <Box sx={{ position: 'relative', height: 150, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                  {r.image
                    ? <img src={r.image} alt={r.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><StorefrontIcon sx={{ fontSize: 52, color: '#cbd5e1' }} /></Box>
                  }
                  {/* Logo bottom-left */}
                  {r.logo && (
                    <Box sx={{ position: 'absolute', bottom: 10, left: 12, width: 46, height: 46, borderRadius: '12px', border: '2.5px solid #fff', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.18)', bgcolor: '#fff' }}>
                      <img src={r.logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  )}
                  {/* Status badge top-right */}
                  <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: '13px !important', color: `${r.isActive ? '#16a34a' : '#d97706'} !important` }} />}
                      label={r.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{ bgcolor: r.isActive ? 'rgba(220,252,231,0.95)' : 'rgba(254,243,199,0.95)', color: r.isActive ? '#16a34a' : '#d97706', fontWeight: 700, fontSize: '0.72rem', height: 22, backdropFilter: 'blur(4px)', '& .MuiChip-icon': { ml: '5px' } }}
                    />
                  </Box>
                </Box>

                {/* Card body */}
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.6 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', lineHeight: 1.3 }}>{r.name}</Typography>
                    {r.shopType && (
                      <Chip label={r.shopType} size="small" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.65rem', height: 20, fontWeight: 600, ml: 0.5, flexShrink: 0 }} />
                    )}
                  </Box>

                  {r.address && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                      <LocationOnIcon sx={{ fontSize: 13, color: '#94a3b8', mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4 }}>{r.address}</Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.2 }}>
                    <PersonIcon sx={{ fontSize: 13, color: '#94a3b8', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: r.owner?.email ? '#475569' : '#94a3b8' }}>
                      {r.owner?.email || 'No vendor'}
                    </Typography>
                  </Box>

                  {/* Stats row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                    {r.reviewCount != null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <StarIcon sx={{ fontSize: 12, color: '#f59e0b' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>{r.reviewCount}</Typography>
                      </Box>
                    )}
                    {r.deliveryTime && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <AccessTimeIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{r.deliveryTime} min</Typography>
                      </Box>
                    )}
                    {r.minimumOrder != null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <ShoppingBagIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>${r.minimumOrder} min</Typography>
                      </Box>
                    )}
                  </Box>

                  <Button
                    fullWidth size="small" variant="outlined"
                    onClick={e => { e.stopPropagation(); mutate({ variables: { id: r._id } }) }}
                    disabled={mutateLoading}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: r.isActive ? '#fca5a5' : '#86efac', color: r.isActive ? '#ef4444' : '#22c55e', '&:hover': { bgcolor: r.isActive ? '#fef2f2' : '#f0fdf4', borderColor: r.isActive ? '#ef4444' : '#22c55e' } }}
                  >
                    {r.isActive ? t('Disable') : t('Enable')}
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Container>

      <Snackbar open={!!snackError} autoHideDuration={5000} onClose={() => setSnackError(null)} message={snackError} />
    </>
  )
}

export default withTranslation()(RestaurantList)